import { DEFAULT_TZ, ENABLE_AVAILABILITY } from '../config/flags'
import { UserAvailability, AvailabilitySnapshot, AvailabilityWindow, DOW } from '../types/availability'

/**
 * Check if availability feature is enabled
 */
export function isAvailabilityEnabled(): boolean {
  return ENABLE_AVAILABILITY
}

/**
 * Detect local timezone or fallback to default
 */
export function detectLocalTz(): string {
  if (!ENABLE_AVAILABILITY) {
    return DEFAULT_TZ
  }
  
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TZ
  } catch {
    return DEFAULT_TZ
  }
}

/**
 * Normalize windows: strip overlap, sort per dow and time
 */
export function normalizeWindows(windows: AvailabilityWindow[]): AvailabilityWindow[] {
  if (!windows.length) return []
  
  // Sort by dow, then by start time
  const sorted = [...windows].sort((a, b) => {
    if (a.dow !== b.dow) return a.dow - b.dow
    return a.start.localeCompare(b.start)
  })
  
  // Remove overlaps and merge adjacent
  const normalized: AvailabilityWindow[] = []
  
  for (const window of sorted) {
    if (window.start >= window.end) continue // Skip invalid windows
    
    const last = normalized[normalized.length - 1]
    if (last && last.dow === window.dow && last.end === window.start) {
      // Merge adjacent windows
      last.end = window.end
    } else {
      normalized.push({ ...window })
    }
  }
  
  return normalized
}

/**
 * Merge adjacent windows with same dow
 */
export function mergeAdjacent(windows: AvailabilityWindow[]): AvailabilityWindow[] {
  return normalizeWindows(windows)
}

/**
 * Convert UserAvailability to AvailabilitySnapshot
 */
export function toSnapshot(av: UserAvailability): AvailabilitySnapshot {
  return {
    tz: av.tz,
    callEnabled: av.callEnabled,
    videoEnabled: av.videoEnabled,
    windows: av.windows,
    updatedAt: av.updatedAt
  }
}

/**
 * Build week grid for UI
 */
export function buildWeekGrid(av: UserAvailability) {
  const days = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za']
  const timeSlots = []
  
  // Generate 30-minute slots from 05:00 to 23:00
  for (let hour = 5; hour < 23; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      timeSlots.push(time)
    }
  }
  
  return { days, timeSlots }
}

/**
 * Clamp windows within office hours
 */
export function withOfficeHours(av: UserAvailability, officeHours?: { start: string, end: string }): UserAvailability {
  if (!officeHours) return av
  
  const clampedWindows = av.windows.map(window => ({
    ...window,
    start: window.start < officeHours.start ? officeHours.start : window.start,
    end: window.end > officeHours.end ? officeHours.end : window.end
  })).filter(window => window.start < window.end)
  
  return {
    ...av,
    windows: clampedWindows
  }
}

/**
 * Check if a time slot is within any availability window
 */
export function isTimeSlotAvailable(windows: AvailabilityWindow[], dow: DOW, time: string): boolean {
  return windows.some(window => 
    window.dow === dow && 
    time >= window.start && 
    time < window.end
  )
}

/**
 * Get available time slots for a specific day
 */
export function getAvailableSlots(windows: AvailabilityWindow[], dow: DOW): string[] {
  const dayWindows = windows.filter(w => w.dow === dow)
  const slots: string[] = []
  
  for (let hour = 5; hour < 23; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      if (isTimeSlotAvailable(dayWindows, dow, time)) {
        slots.push(time)
      }
    }
  }
  
  return slots
}
